/**
 * Drag and Drop module
 * @constructor
 * @param {array} draggable - list of draggable elements
 * @param {array} droppable - list of droppable elements
 * @param {string} dataImage - src url
 * @param {function} generateTooltip - tooltip generate function
 * @param {function} onDragStart - onDragStart function
 * @param {function} onDragEnd - onDragEnd function
 * @param {function} onDragOver - onDragOver function
 * @param {function} onDragEnter - onDragEnter function
 * @param {function} onDragLeave - onDragLeave function
 * @param {function} onDrop - onDrop function
 */

/**
 * add this link/css to html file
 * <link rel="stylesheet" type="text/css" href="/WebInterface/new-ui/modules/drag-and-drop/drag-and-drop.css" />
 */

import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";

export default class DragDrop {
  constructor({
    draggable = [],
    droppable = [],
    dataImage = "data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=",
    generateTooltip,
    onDragStart = () => {},
    onDragEnd = () => {},
    onDragOver = () => {},
    onDragEnter = () => {},
    onDragLeave = () => {},
    onDrop = () => {},
  }) {
    this.draggable = draggable;
    this.droppable = droppable;
    this.dataImage = dataImage;
    this.generateTooltip = generateTooltip;
    this.onDragStart = onDragStart;
    this.onDragOver = onDragOver;
    this.onDragEnter = onDragEnter;
    this.onDragEnd = onDragEnd;
    this.onDragLeave = onDragLeave;
    this.onDrop = onDrop;
    if (this.generateTooltip) {
      this.tooltip = document.getElementById("dndTooltip");
      if (!this.tooltip) {
        this.tooltip = document.createElement("div");
        this.tooltip.id = "dndTooltip";
        document.body.appendChild(this.tooltip);
      }
    }
    this.init();
  }

  init = () => {
    const that = this;
    that.handleDragstart = (event, element) => {
      if (that.generateTooltip) {
        that.tooltip.classList.remove("is-hidden");
        that.tooltip.innerHTML = that.generateTooltip(element);
      }
      event.dataTransfer.draggedElem = element;
      that.onDragStart(event, element);
    };

    that.handleDragend = (event, element) => {
      that.tooltip.removeAttribute("style");
      that.tooltip.innerHTML = "";
      this.hideTooltip();
      that.onDragEnd(event, element);
    };

    that.handleDrag = (event, element) => {
      event.preventDefault();
      if (event.clientX && event.clientY) {
        // Round dynamic values to nearest 10px for utility class compatibility
        const roundedLeft = Math.round((event.clientX + 20) / 10) * 10;
        const roundedTop = Math.round((event.clientY + 20) / 10) * 10;
        const roundedRight = Math.round((innerWidth - event.clientX) / 10) * 10;
        
        that.tooltip.classList.add("is-flex");
        that.tooltip.classList.remove("d-n");
        _$(that.tooltip).css("left", `${roundedLeft}px`);
        _$(that.tooltip).css("top", `${roundedTop}px`);
        _$(that.tooltip).css("bottom", "auto");
        _$(that.tooltip).css("right", "auto");
        // Remove transform property (CSP-compliant)
        that.tooltip.style.removeProperty("transform");
        
        if (that.tooltip.getBoundingClientRect().right > innerWidth - 10) {
          _$(that.tooltip).css("left", "auto");
          _$(that.tooltip).css("right", `${roundedRight}px`);
        }
        if (that.tooltip.getBoundingClientRect().bottom > innerHeight) {
          _$(that.tooltip).css("top", "auto");
          _$(that.tooltip).css("bottom", "0");
        }
      }
    };

    that.draggable.forEach((element) => {
      element.draggable = true;
      if (!element.getAttribute("data-dnd-drag")) {
        element.addEventListener("dragstart", (event) =>
          that.handleDragstart(event, element)
        );
        element.addEventListener("dragend", (event) =>
          that.handleDragend(event, element)
        );
        element.addEventListener("drag", (event) =>
          that.handleDrag(event, element)
        );
        element.setAttribute("data-dnd-drag", true);
      }
    });

    that.handleDrop = (event, targetElement) => {
      event.preventDefault();
      that.onDrop(event, targetElement);
    };

    that.handleDragenter = (event, targetElement) => {
      event.preventDefault();
      that.onDragEnter(event, targetElement);
    };

    that.handleDragleave = (event, targetElement) => {
      event.preventDefault();
      that.onDragLeave(event, targetElement);
    };

    that.handleDragover = (event, targetElement) => {
      event.preventDefault();
      that.onDragOver(event, targetElement);
    };

    that.droppable.forEach((targetElement) => {
      if (targetElement && !targetElement.getAttribute("data-dnd-drop")) {
        targetElement.addEventListener("dragover", (event) =>
          that.handleDragover(event, targetElement)
        );
        targetElement.addEventListener("dragleave", (event) =>
          that.handleDragleave(event, targetElement)
        );
        targetElement.addEventListener("dragenter", (event) =>
          that.handleDragenter(event, targetElement)
        );
        targetElement.addEventListener("drop", (event) =>
          that.handleDrop(event, targetElement)
        );
        targetElement.setAttribute("data-dnd-drop", true);
      }
    });
  }

  removeEventListener = () => {
    const that = this;
    that.draggable.forEach((element) => {
      element.draggable = false;
      if (element.getAttribute("data-dnd-drag")) {
        element.removeEventListener("dragstart", (event) =>
          that.handleDragstart(event, element)
        );
        element.removeEventListener("dragend", (event) =>
          that.handleDragend(event, element)
        );
        element.removeEventListener("drag", (event) =>
          that.handleDrag(event, element)
        );
        element.setAttribute("data-dnd-drag", false);
      }
    });

    that.droppable.forEach((targetElement) => {
      if (targetElement.getAttribute("data-dnd-drop")) {
        targetElement.removeEventListener("dragover", (event) =>
          that.handleDragover(event, targetElement)
        );
        targetElement.removeEventListener("dragleave", (event) =>
          that.handleDragleave(event, targetElement)
        );
        targetElement.removeEventListener("dragenter", (event) =>
          that.handleDragenter(event, targetElement)
        );
        targetElement.removeEventListener("drop", (event) =>
          that.handleDrop(event, targetElement)
        );
        targetElement.setAttribute("data-dnd-drop", false);
      }
    });
  }

  setDragPreview = (event, image) => {
    const that = this;
    let img = image || new Image();
    img.src = that.dataImage;
    event.dataTransfer.setDragImage(img, 0, 0);
  }

  hideTooltip = () => {
    const that = this;
    that.tooltip.classList.add("is-hidden");
  }
}
